import React, { FC, useCallback, useMemo, useState } from 'react';
import { DocsNodeType } from 'src/models/documentsLibrary/types';
import {
  useUpdateNavFoldersContext,
  useUpdateCurrentFolderContext,
  useNavFoldersContext,
  useActiveDocNode
} from 'src/models/documentsLibrary/hooks';
import styles from './index.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faFolderOpen, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FileStatus, IDocumentsTableProps } from '../../types';
import { getIconByFileType } from 'src/helpers/fileTypeIcon';
import classNames from 'classnames';
import { DocumentViewer } from 'src/shared';
import { downloadFile, splitFileExt } from 'src/helpers/files';
import Spinner from 'src/common/Spinner';
import { useTranslation } from 'react-i18next';
import { trackViewDocumentPreview } from 'src/segment/events';

interface IProps {
  docsNode: IDocumentsTableProps;
  eventSource: string;
  procurementId?: string;
}

export const DocumentsNodeName: FC<IProps> = props => {
  const { docsNode, eventSource, procurementId } = props;
  const { id, name, nodeType, getUrl, status } = docsNode;
  const updateNavFolders = useUpdateNavFoldersContext();
  const updateCurrentFolder = useUpdateCurrentFolderContext();
  const [activeDocNode] = useActiveDocNode();
  const navFolders = useNavFoldersContext();
  const [, fileExt] = splitFileExt(name);
  const isUpdating = useMemo(() => activeDocNode === id, [activeDocNode, id]);
  const extIcon = useMemo(() => {
    return nodeType === DocsNodeType.FOLDER ? (
      <>
        <FontAwesomeIcon icon={faFolder} className={classNames(styles.extIcon, styles.folderIcon)} />
        <FontAwesomeIcon icon={faFolderOpen} className={classNames(styles.extIcon, styles.openFolderIcon)} />
      </>
    ) : (
      getIconByFileType(fileExt)
    );
  }, [fileExt, nodeType]);

  const [modalVisible, setModalVisible] = useState(false);

  const onShowModal = useCallback(() => {
    trackViewDocumentPreview(eventSource, procurementId ? [procurementId] : undefined);
    setModalVisible(true);
  }, [eventSource, procurementId]);

  const onHideModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  const onDownload = useCallback(() => {
    !!getUrl && downloadFile(getUrl, name);
  }, [getUrl, name]);

  const onFolderClick = useCallback(() => {
    const updatedNavFolders = [...navFolders, { id, name }];
    updateNavFolders?.(updatedNavFolders);
    updateCurrentFolder?.({ id, name });
  }, [navFolders, id, name, updateNavFolders, updateCurrentFolder]);

  const onFileClick = useCallback(() => {
    if (!getUrl) return;
    if (fileExt === 'pdf') {
      onShowModal();
    } else {
      onDownload();
    }
  }, [fileExt, getUrl, onDownload, onShowModal]);

  const { t } = useTranslation();

  return (
    <>
      <div
        className={classNames(styles.docsTitle, { [styles.docsFolder]: nodeType === DocsNodeType.FOLDER })}
        onClick={nodeType === DocsNodeType.FOLDER ? onFolderClick : onFileClick}
      >
        <span className={styles.docsIcon}>{extIcon}</span>
        <span className={styles.docsName}>{name}</span>
        {isUpdating && <Spinner className={styles.spinner} />}
        {status && status === FileStatus.WaitingForUpload && (
          <div className={styles.warning}>
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span>{t('DocumentLibrary.failedToUpload')}</span>
          </div>
        )}
      </div>
      {modalVisible && <DocumentViewer onClose={onHideModal} url={getUrl} title={name} />}
    </>
  );
};

export default DocumentsNodeName;
